import { useEffect, useState } from 'react';
import {
  getScheduleCapacity,
  type CapacityDayEntry,
  type CapacityStage,
  type CapacityWorkCenter,
  type ScheduleCapacityResult,
} from '@/api/productionOrder.api';

interface ScheduleCapacityCheckProps {
  routingId: string;
  qty: number;
  plannedStart: string;
  plannedEnd: string;
  warehouseId?: string;
  /** Editing an order: its own work orders are not counted as existing load */
  excludeOrderId?: string;
  unit?: string;
  onApplyDates: (start: string, end: string) => void;
}

const hr = (minutes: number) => `${(Math.round((minutes / 60) * 10) / 10).toLocaleString('en-IN')} hr`;
const mins = (minutes: number) => `${(Math.round(minutes * 10) / 10).toLocaleString('en-IN')} min`;

function formatDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

// "100 × 5 min" (per unit) or "⌈100 ÷ 50⌉ × 60 min" (per batch), plus setup
function timeExpr(qty: number, basis: string | undefined, batchSize: number | undefined, run: number | null | undefined, setup = 0): string {
  const runPart = basis === 'PER_BATCH'
    ? `⌈${qty} ÷ ${batchSize || 1}⌉ batches × ${mins(run || 0)}`
    : `${qty} × ${mins(run || 0)}`;
  return setup > 0 ? `${mins(setup)} setup + ${runPart}` : runPart;
}

function StageLines({ stages, qty, unit }: { stages: CapacityStage[]; qty: number; unit?: string }) {
  return (
    <>
      {stages.map((st) => (
        <div key={st.stageNumber} className="flex items-baseline justify-between gap-3">
          <span className="text-indigo-700">
            This order · {st.stageName}: <span className="font-mono">{timeExpr(qty, st.basis, st.batchSize, st.runMinutes, st.setupMinutes)}</span>
            {unit ? <span className="text-[#94a3b8]"> ({unit})</span> : null}
          </span>
          <span className="font-mono text-[#1e293b] whitespace-nowrap">= {mins(st.minutes)}</span>
        </div>
      ))}
    </>
  );
}

function EntryLine({ entry }: { entry: CapacityDayEntry }) {
  const full = entry.minutes * entry.spreadDays;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[#475569]">
        {entry.poNumber || entry.woNumber}
        {entry.itemName ? ` · ${entry.itemName}` : ''}:{' '}
        <span className="font-mono">{entry.runMinutes != null ? timeExpr(entry.qty, entry.basis, entry.batchSize, entry.runMinutes) : `${entry.qty} pcs`}</span>
      </span>
      <span className="font-mono text-[#1e293b] whitespace-nowrap">
        {entry.spreadDays > 1 ? `${mins(full)} ÷ ${entry.spreadDays} days = ` : '= '}
        {mins(entry.minutes)}
      </span>
    </div>
  );
}

function WorkCenterCard({ wc, qty, unit }: { wc: CapacityWorkCenter; qty: number; unit?: string }) {
  const peak = wc.peakDay;
  if (!peak) return null;
  const others = peak.entries.filter((e) => !e.isThisOrder);
  const self = peak.entries.find((e) => e.isThisOrder);
  const over = peak.overload;

  return (
    <div className={`rounded-lg border p-3 text-[11px] space-y-2.5 ${over ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200 bg-slate-50/40'}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-[#1e293b]">
          {wc.code ? `${wc.code} · ` : ''}{wc.name}
        </p>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${over ? 'bg-rose-100 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
          <i className={over ? 'ri-alert-line' : 'ri-checkbox-circle-line'} />
          {over ? `Overloaded${wc.overloadedDays.length > 1 ? ` · ${wc.overloadedDays.length} days` : ''}` : 'Fits'}
        </span>
      </div>

      {/* Available time — Work Center master */}
      <div>
        <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wide mb-0.5">Available time (Work Center)</p>
        <p className="font-mono text-[#1e293b]">
          {wc.hoursPerDay} hr × {wc.efficiencyPct}% efficiency × {wc.capacityUnits} {wc.machineCount > 0 ? `machine${wc.capacityUnits > 1 ? 's' : ''}` : 'station (no machines)'}
          {' '}= <span className="font-bold">{hr(wc.availableMinutesPerDay)} / day</span>
        </p>
      </div>

      {/* Required time — Routing */}
      <div>
        <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wide mb-0.5">
          Required time (Routing) · {wc.orderDays > 1 ? `busiest day ${formatDay(peak.date)}` : formatDay(peak.date)}
        </p>
        <div className="space-y-0.5">
          {others.map((e, i) => <EntryLine key={`${e.woNumber}-${i}`} entry={e} />)}
          <StageLines stages={wc.stages} qty={qty} unit={unit} />
          {self && self.spreadDays > 1 && (
            <div className="flex justify-end">
              <span className="font-mono text-indigo-700">This order: {mins(wc.orderMinutes)} ÷ {self.spreadDays} days = {mins(self.minutes)} / day</span>
            </div>
          )}
          <div className="flex items-baseline justify-between gap-3 border-t border-slate-200 pt-1 mt-1">
            <span className="font-semibold text-[#1e293b]">Total</span>
            <span className="font-mono font-bold text-[#1e293b]">{mins(peak.totalMinutes)} = {hr(peak.totalMinutes)}</span>
          </div>
        </div>
      </div>

      <p className={`font-mono font-semibold ${over ? 'text-rose-700' : 'text-emerald-700'}`}>
        {hr(peak.totalMinutes)} {over ? '>' : '≤'} {hr(peak.availableMinutes)} → {over ? '⚠️ Overload' : '✓ Within capacity'}
        {over && wc.minDaysNeeded && wc.minDaysNeeded > wc.orderDays
          ? <span className="font-sans font-normal text-rose-600"> · this order alone needs {wc.minDaysNeeded} working days here</span>
          : null}
      </p>
    </div>
  );
}

export default function ScheduleCapacityCheck({
  routingId, qty, plannedStart, plannedEnd, warehouseId, excludeOrderId, unit, onApplyDates,
}: ScheduleCapacityCheckProps) {
  const [result, setResult] = useState<ScheduleCapacityResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  const ready = Boolean(routingId && plannedStart && qty > 0);

  useEffect(() => {
    if (!ready) { setResult(null); return; }
    let cancelled = false;
    // Debounced: qty and dates change as the user types
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getScheduleCapacity({
          routingId, qty, startDate: plannedStart, endDate: plannedEnd || undefined, warehouseId, excludeOrderId,
        });
        if (!cancelled) setResult(res.data ?? null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not check capacity');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [ready, routingId, qty, plannedStart, plannedEnd, warehouseId, excludeOrderId]);

  if (!ready) return null;

  const centers = result?.workCenters ?? [];
  const overloaded = !!result?.overloaded;

  return (
    <div className={`mt-4 rounded-xl border ${overloaded ? 'border-rose-200' : 'border-slate-200'} bg-white`}>
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 cursor-pointer"
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-[#1e293b]">
          <i className="ri-dashboard-3-line text-[#4f46e5]" />
          Capacity Check
          {loading && <i className="ri-loader-4-line animate-spin text-[#94a3b8]" />}
          {!loading && result && centers.length > 0 && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${overloaded ? 'bg-rose-100 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
              {overloaded ? '⚠️ Overload' : '✓ Capacity available'}
            </span>
          )}
        </span>
        <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-[#94a3b8]`} />
      </button>

      {expanded && (
        <div className="px-3.5 pb-3.5 space-y-3">
          <p className="text-[11px] text-[#64748b]">
            Available / day = working hours × efficiency × machines · Required = qty × time per unit from routing, for all orders on that day.
          </p>

          {error && <p className="text-[11px] text-rose-600">{error}</p>}

          {result && centers.length === 0 && !loading && (
            <p className="text-[11px] text-[#94a3b8]">No in-house routing stage with a work center — nothing to check.</p>
          )}

          {centers.map((wc) => <WorkCenterCard key={wc.workCenterId} wc={wc} qty={result!.orderQty} unit={unit} />)}

          {overloaded && (result?.suggestion || result?.extendSuggestion) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
              <p className="text-[11px] font-semibold text-amber-900">
                <i className="ri-lightbulb-line" /> Resolve the overload
              </p>
              <div className="flex flex-wrap gap-2">
                {result?.suggestion && (
                  <button
                    type="button"
                    onClick={() => onApplyDates(result.suggestion!.startDate, result.suggestion!.endDate)}
                    className="h-8 px-3 rounded-lg bg-[#4f46e5] text-white text-[11px] font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap"
                  >
                    Shift order to {formatDay(result.suggestion.startDate)} (+{result.suggestion.shiftDays} day{result.suggestion.shiftDays > 1 ? 's' : ''})
                  </button>
                )}
                {result?.extendSuggestion && (
                  <button
                    type="button"
                    onClick={() => onApplyDates(result.extendSuggestion!.startDate, result.extendSuggestion!.endDate)}
                    className="h-8 px-3 rounded-lg border border-amber-300 bg-white text-amber-900 text-[11px] font-semibold hover:bg-amber-100 cursor-pointer whitespace-nowrap"
                  >
                    Keep start, extend end to {formatDay(result.extendSuggestion.endDate)} (+{result.extendSuggestion.extraDays} day{result.extendSuggestion.extraDays > 1 ? 's' : ''})
                  </button>
                )}
              </div>
            </div>
          )}
          {overloaded && !result?.suggestion && !result?.extendSuggestion && (
            <p className="text-[11px] text-rose-600">No free slot found in the next 60 days — add machines or hours on the work center, or split the order.</p>
          )}
        </div>
      )}
    </div>
  );
}
